export type MeasurementStage = 'rendered' | 'viewability_candidate' | 'impression';
export type PaintObservation = { painted: boolean; visibleFraction: number; foreground: boolean };

/** Per-delivery measurement. Sending never establishes visibility by itself. */
export class DeliveryMeasurement {
  private visibleSince: number | undefined;
  private completed = new Set<MeasurementStage>();
  private attempts = new Map<MeasurementStage, number>();
  private retryAt = 0;
  private inFlight = false;
  private disposed = false;
  private controller = new AbortController();
  constructor(
    private receivedAt: number,
    private send: (stage: MeasurementStage, signal: AbortSignal) => Promise<unknown>,
  ) {}
  pause() {
    this.visibleSince = undefined;
  }
  observe(observation: PaintObservation, now: number) {
    if (this.disposed) return;
    const viewable = observation.foreground && observation.painted && observation.visibleFraction >= 0.5;
    if (viewable) this.visibleSince ??= now;
    else this.pause();
    if (!observation.foreground || !observation.painted || this.inFlight || now < this.retryAt) return;
    const stage: MeasurementStage | undefined = !this.completed.has('rendered')
      ? 'rendered'
      : !this.completed.has('viewability_candidate') && viewable
        ? 'viewability_candidate'
        : !this.completed.has('impression') &&
            this.completed.has('viewability_candidate') &&
            viewable &&
            this.visibleSince !== undefined &&
            now - this.visibleSince >= 1000 &&
            now - this.receivedAt >= 1100
          ? 'impression'
          : undefined;
    if (!stage || (this.attempts.get(stage) || 0) >= 3) return;
    const attempt = (this.attempts.get(stage) || 0) + 1;
    this.attempts.set(stage, attempt);
    this.inFlight = true;
    this.send(stage, this.controller.signal)
      .then(() => {
        if (!this.disposed) this.completed.add(stage);
      })
      .catch(() => {
        if (!this.disposed) this.retryAt = now + attempt * 1000;
      })
      .finally(() => {
        this.inFlight = false;
      });
  }
  dispose() {
    this.disposed = true;
    this.pause();
    this.controller.abort();
  }
}
